/**
 * Timing for the homepage How It Works audit animation.
 * Each tab (ARO, AEO, CRO) plays URL → that audit → scan → results.
 */

import { AEO_BENCHMARKS } from "~/lib/siteglue/benchmarks/catalog";
import { ARO_BENCHMARK_COUNT, CRO_BENCHMARK_COUNT } from "~/lib/siteglue/landing-redesign/copy";
import {
  HOMEPAGE_PREVIEW_SLIDES,
  type HomepagePreviewSlideId,
} from "~/lib/siteglue/landing-redesign/homepage-hero";

export const AUDIT_DEMO_LOOP_MS = 22_000;
export const AUDIT_DEMO_DOMAIN = "yourcompany.com";
export const AUDIT_DEMO_ARO_COUNT = ARO_BENCHMARK_COUNT;
export const AUDIT_DEMO_AEO_COUNT = AEO_BENCHMARKS.length;
export const AUDIT_DEMO_CRO_COUNT = CRO_BENCHMARK_COUNT;
export const AUDIT_DEMO_HRO_COUNT = AUDIT_DEMO_CRO_COUNT;
export const AUDIT_DEMO_CHANNEL_COUNT = 8;
export const AUDIT_DEMO_TAB_ORDER = ["aro", "aeo", "cro"] as const satisfies readonly HomepagePreviewSlideId[];
export type AuditDemoTabId = (typeof AUDIT_DEMO_TAB_ORDER)[number];

export type AuditDemoSceneId = "url" | "reports" | "scan" | "results";

export type AuditDemoScene = {
  id: AuditDemoSceneId;
  startMs: number;
  endMs: number;
};

export const AUDIT_DEMO_SCENES: readonly AuditDemoScene[] = [
  { id: "url", startMs: 0, endMs: 3_800 },
  { id: "reports", startMs: 3_800, endMs: 9_600 },
  { id: "scan", startMs: 9_600, endMs: 12_400 },
  { id: "results", startMs: 12_400, endMs: 22_000 },
] as const;

export const AUDIT_DEMO_AGENTS = [
  { id: "claude", label: "Claude Duet", provider: "Anthropic" },
  { id: "grok", label: "Grok Bot", provider: "xAI" },
  { id: "dots", label: "Dots", provider: "OpenAI" },
  { id: "muse", label: "Muse", provider: "Meta" },
  { id: "openclaw", label: "OpenClaw", provider: "OpenClaw" },
] as const;

export const AUDIT_DEMO_CHANNELS = [
  { id: "website", label: "Website", help: "Website chat with an AI Employee that can receive a message and reply." },
  { id: "phone", label: "Phone / Front Desk", help: "An AI Employee that answers the phone." },
  { id: "sms", label: "Text Messages", help: "An AI Employee that receives texts and replies." },
  { id: "email", label: "Email", help: "An AI Employee that receives email and replies." },
] as const;

export const AUDIT_DEMO_ARO_SLIDE = HOMEPAGE_PREVIEW_SLIDES.find((slide) => slide.id === "aro")!;
export const AUDIT_DEMO_AEO_SLIDE = HOMEPAGE_PREVIEW_SLIDES.find((slide) => slide.id === "aeo")!;
export const AUDIT_DEMO_CRO_SLIDE = HOMEPAGE_PREVIEW_SLIDES.find((slide) => slide.id === "cro")!;
export const AUDIT_DEMO_CHANNELS_SLIDE = AUDIT_DEMO_CRO_SLIDE;
export const AUDIT_DEMO_HRO_SLIDE = AUDIT_DEMO_CRO_SLIDE;

export function nextAuditDemoTab(current: AuditDemoTabId): AuditDemoTabId {
  const index = AUDIT_DEMO_TAB_ORDER.indexOf(current);
  return AUDIT_DEMO_TAB_ORDER[(index + 1) % AUDIT_DEMO_TAB_ORDER.length] ?? "aro";
}

export function auditDemoSlideForTab(tab: AuditDemoTabId) {
  if (tab === "aeo") return AUDIT_DEMO_AEO_SLIDE;
  if (tab === "cro") return AUDIT_DEMO_CRO_SLIDE;
  return AUDIT_DEMO_ARO_SLIDE;
}

export function auditDemoCheckCountForTab(tab: AuditDemoTabId): number {
  if (tab === "aeo") return AUDIT_DEMO_AEO_COUNT;
  if (tab === "cro") return AUDIT_DEMO_CRO_COUNT;
  return AUDIT_DEMO_ARO_COUNT;
}

export function wrapAuditDemoElapsed(elapsedMs: number): number {
  return ((elapsedMs % AUDIT_DEMO_LOOP_MS) + AUDIT_DEMO_LOOP_MS) % AUDIT_DEMO_LOOP_MS;
}

export function auditDemoSceneAt(elapsedMs: number): AuditDemoSceneId {
  const t = wrapAuditDemoElapsed(elapsedMs);
  const scene = AUDIT_DEMO_SCENES.find((item) => t >= item.startMs && t < item.endMs);
  return scene?.id ?? "url";
}

export function auditDemoSceneProgress(elapsedMs: number, sceneId: AuditDemoSceneId): number {
  const scene = AUDIT_DEMO_SCENES.find((item) => item.id === sceneId);
  if (!scene) return 0;
  const t = wrapAuditDemoElapsed(elapsedMs);
  if (t < scene.startMs) return 0;
  if (t >= scene.endMs) return 1;
  return (t - scene.startMs) / (scene.endMs - scene.startMs);
}

export function auditDemoTypedDomain(
  elapsedMs: number,
  domain = AUDIT_DEMO_DOMAIN,
): string {
  const t = wrapAuditDemoElapsed(elapsedMs);
  if (auditDemoSceneAt(t) !== "url") return domain;
  const typeStart = 480;
  const typeEnd = 2_200;
  if (t <= typeStart) return "";
  if (t >= typeEnd) return domain;
  const progress = (t - typeStart) / (typeEnd - typeStart);
  return domain.slice(0, Math.max(0, Math.round(progress * domain.length)));
}

export function auditDemoEaseOut(progress: number): number {
  const p = Math.min(1, Math.max(0, progress));
  return 1 - (1 - p) ** 3;
}

export function auditDemoCountTo(target: number, progress: number): number {
  return Math.round(target * auditDemoEaseOut(progress));
}

export function auditDemoShouldAutoplay(options: {
  reducedMotion: boolean;
  inView: boolean;
  pageVisible: boolean;
  paused: boolean;
}): boolean {
  return !options.reducedMotion && options.inView && options.pageVisible && !options.paused;
}

export function auditDemoReducedMotionElapsed(): number {
  const results = AUDIT_DEMO_SCENES.find((scene) => scene.id === "results");
  return results ? results.endMs - 200 : AUDIT_DEMO_LOOP_MS - 200;
}

export function auditDemoCheckedCount(total: number, progress: number): number {
  return Math.min(total, Math.max(0, Math.round(progress * total)));
}

export function auditDemoResultReveal(elapsedMs: number): {
  aro: number;
  aeo: number;
  cro: number;
  channels: number;
  hro: number;
} {
  const progress = auditDemoSceneProgress(elapsedMs, "results");
  const cro = Math.min(1, Math.max(0, (progress - 0.18) / 0.5));
  return {
    aro: Math.min(1, progress / 0.48),
    aeo: Math.min(1, progress / 0.55),
    cro,
    channels: Math.min(1, Math.max(0, (progress - 0.32) / 0.55)),
    hro: cro,
  };
}
